import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  BarChart,
  Bar,
  AreaChart,
  Area,
  ReferenceLine,
} from "recharts";
import { Crown, BarChart3, LineChart as LineIcon, Layers, ChevronDown } from "lucide-react";

function normDomain(s) {
  return (s || "").trim().replace(/^https?:\/\//, "").replace(/\/$/, "").toLowerCase();
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Palette: client gets teal, others rotate through these
const PALETTE = ["#38b2ac", "#6366f1", "#f59e0b", "#ec4899", "#10b981", "#f43f5e", "#0ea5e9", "#a855f7"];

function colorFor(domains, clientDomainKey) {
  const map = {};
  let i = 0;
  for (const d of domains) {
    if (d === clientDomainKey) {
      map[d] = "#38b2ac"; // brand teal
    } else {
      // skip teal slot for non-clients
      let c = PALETTE[(i + 1) % PALETTE.length];
      if (c === "#38b2ac") c = PALETTE[(i + 2) % PALETTE.length];
      map[d] = c;
      i += 1;
    }
  }
  return map;
}

const RANGE_OPTIONS = [
  { value: "all", label: "All history" },
  { value: "4w", label: "Last 4 weeks", days: 28 },
  { value: "12w", label: "Last 12 weeks", days: 84 },
  { value: "6m", label: "Last 6 months", days: 183 },
];

function filterByRange(snapshots, range) {
  if (range === "all") return snapshots;
  const opt = RANGE_OPTIONS.find((o) => o.value === range);
  if (!opt?.days) return snapshots;
  const cutoff = Date.now() - opt.days * 24 * 60 * 60 * 1000;
  return snapshots.filter((s) => new Date(s.at).getTime() >= cutoff);
}

// Compute union of domains seen across snapshots, client first
function collectDomains(snapshots, clientDomain) {
  const seen = new Map(); // key -> { key, label, is_client }
  const clientKey = normDomain(clientDomain);
  for (const s of snapshots) {
    for (const t of s.targets || []) {
      const k = normDomain(t.domain);
      if (!seen.has(k)) {
        seen.set(k, { key: k, label: t.domain, is_client: !!t.is_client });
      }
    }
  }
  // Ensure client always present (even if no snapshot has it)
  if (!seen.has(clientKey)) {
    seen.set(clientKey, { key: clientKey, label: clientDomain, is_client: true });
  }
  // Sort: client first then alpha
  return [...seen.values()].sort((a, b) => {
    if (a.is_client && !b.is_client) return -1;
    if (!a.is_client && b.is_client) return 1;
    return a.key.localeCompare(b.key);
  });
}

function CardHeader({ icon: Icon, kicker, title, right }) {
  return (
    <div className="px-5 py-4 border-b border-[#edf2f7] flex items-end justify-between gap-3">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-[#4fd1c5]/10 text-[#2c7a7b] inline-flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">{kicker}</div>
          <h3 className="font-display text-base mt-0.5 text-[#1a202c]">{title}</h3>
        </div>
      </div>
      {right}
    </div>
  );
}

function EmptyState({ msg }) {
  return (
    <div className="h-[260px] flex items-center justify-center text-xs text-[#a0aec0] font-mono-tight px-5">
      {msg}
    </div>
  );
}

function TooltipBox({ active, payload, label, valueFmt }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl shadow-sm px-3 py-2 text-xs">
      <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold mb-1">{label}</div>
      <div className="space-y-0.5">
        {payload.map((p) => (
          <div key={p.dataKey} className="flex items-center gap-2 font-mono-tight">
            <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
            <span className="text-[#4a5568] truncate max-w-[180px]">{p.name}</span>
            <span className="ml-auto text-[#1a202c] font-semibold">{valueFmt ? valueFmt(p.value) : (p.value ?? "—")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CompetitorHistoryGraphs({ snapshots, clientDomain }) {
  const [range, setRange] = useState("all");
  const clientKey = normDomain(clientDomain);

  // snapshots are returned newest-first by API → reverse for charts
  const ordered = useMemo(() => [...(snapshots || [])].reverse(), [snapshots]);
  const ranged = useMemo(() => filterByRange(ordered, range), [ordered, range]);
  const domains = useMemo(() => collectDomains(ranged, clientDomain), [ranged, clientDomain]);
  const colors = useMemo(() => colorFor(domains.map((d) => d.key), clientKey), [domains, clientKey]);

  // Visibility series: [{ at, label, [domain]: top10_pct, ... }]
  const visibilityData = useMemo(() => {
    return ranged.map((s) => {
      const row = { at: s.at, label: fmtDate(s.at) };
      for (const d of domains) row[d.key] = (s.sov?.[d.key]?.top10_pct ?? null);
      return row;
    });
  }, [ranged, domains]);

  // Domain rank series (lower = better, so we'll invert axis)
  const domainRankData = useMemo(() => {
    return ranged.map((s) => {
      const row = { at: s.at, label: fmtDate(s.at) };
      const byDom = {};
      for (const t of s.targets || []) byDom[normDomain(t.domain)] = t.overview?.rank ?? null;
      for (const d of domains) row[d.key] = byDom[d.key] ?? null;
      return row;
    });
  }, [ranged, domains]);

  // Detect "rank data unavailable for every target" — happens when the DFS
  // account doesn't have the Backlinks tier enabled (where the DFS Rank score
  // lives). Without this check, the chart renders empty axes and confuses the user.
  const domainRankHasAnyValue = useMemo(() => {
    for (const row of domainRankData) {
      for (const d of domains) {
        if (row[d.key] != null) return true;
      }
    }
    return false;
  }, [domainRankData, domains]);

  // Determine "Top 5 keywords" from the latest snapshot in range:
  //  pick the 5 keywords where the client has best (lowest) rank; fallback to first 5
  const latest = ranged[ranged.length - 1];
  const top5Keywords = useMemo(() => {
    if (!latest) return [];
    const rows = latest.rows || [];
    const scored = rows.map((r) => ({
      keyword: r.keyword,
      clientPos: r.positions?.[clientKey] ?? null,
    }));
    const ranked = scored
      .filter((x) => x.clientPos != null)
      .sort((a, b) => a.clientPos - b.clientPos);
    const remaining = scored.filter((x) => x.clientPos == null);
    return [...ranked, ...remaining].slice(0, 5).map((x) => x.keyword);
  }, [latest, clientKey]);

  // Top 5 grouped bar: for each keyword, one bar per domain (rank position; invert axis for visual)
  const top5BarData = useMemo(() => {
    if (!latest) return [];
    return top5Keywords.map((kw) => {
      const row = latest.rows.find((r) => r.keyword === kw);
      const out = { keyword: kw };
      for (const d of domains) {
        const p = row?.positions?.[d.key];
        // Unranked → null so Recharts omits the bar (avoids fake "rank 0" tall bar with reversed axis)
        out[d.key] = p == null ? null : p;
        out[`${d.key}__raw`] = p ?? null;
      }
      return out;
    });
  }, [latest, top5Keywords, domains]);

  // Stacked area: rank distribution buckets over time for a *selected* target
  const [stackTarget, setStackTarget] = useState(clientKey);
  const stackData = useMemo(() => {
    const tk = normDomain(stackTarget);
    return ranged.map((s) => {
      const buckets = { top3: 0, top10: 0, top30: 0, beyond: 0, unranked: 0 };
      for (const r of s.rows || []) {
        const p = r.positions?.[tk];
        if (p == null) buckets.unranked += 1;
        else if (p <= 3) buckets.top3 += 1;
        else if (p <= 10) buckets.top10 += 1;
        else if (p <= 30) buckets.top30 += 1;
        else buckets.beyond += 1;
      }
      return { at: s.at, label: fmtDate(s.at), ...buckets };
    });
  }, [ranged, stackTarget]);

  const snapshotCount = ordered.length;
  const rangeCount = ranged.length;

  // Range selector
  const RangeSelector = (
    <div className="inline-flex items-center gap-1 p-1 bg-[#f7fafc] border border-[#edf2f7] rounded-xl">
      {RANGE_OPTIONS.map((o) => (
        <button
          key={o.value}
          onClick={() => setRange(o.value)}
          data-testid={`history-range-${o.value}`}
          className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-lg transition-colors ${
            range === o.value
              ? "bg-white text-[#1a202c] shadow-sm border border-[#e2e8f0]"
              : "text-[#718096] hover:text-[#1a202c]"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );

  if (snapshotCount === 0) {
    return (
      <div className="card-soft p-6 text-center" data-testid="competitor-history-empty">
        <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
          Historical performance
        </div>
        <h3 className="font-display text-base mt-1 text-[#1a202c]">No history yet</h3>
        <p className="text-xs text-[#718096] mt-2 max-w-md mx-auto">
          The weekly tracker runs every Monday and saves a snapshot automatically. You can also run
          a comparison above and click <span className="font-semibold text-[#1a202c]">Save snapshot</span> to seed the chart history.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="competitor-history">
      {/* Section header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
            Historical performance
          </div>
          <h3 className="font-display text-2xl mt-0.5 text-[#1a202c]">Trends across snapshots</h3>
          <p className="text-xs text-[#718096] mt-1">
            Auto-tracked weekly. Showing {rangeCount} of {snapshotCount} snapshot{snapshotCount === 1 ? "" : "s"}.
          </p>
        </div>
        {RangeSelector}
      </div>

      {/* Search visibility line chart */}
      <div className="card-soft" data-testid="chart-search-visibility">
        <CardHeader
          icon={LineIcon}
          kicker="Search visibility"
          title="Share of voice (Top 10 %)"
          right={<DomainLegend domains={domains} colors={colors} />}
        />
        {rangeCount < 1 ? (
          <EmptyState msg="No snapshots in this range." />
        ) : (
          <div className="p-3 h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={visibilityData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#a0aec0" }} stroke="#cbd5e0" />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 10, fill: "#a0aec0" }}
                  stroke="#cbd5e0"
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip content={<TooltipBox valueFmt={(v) => v == null ? "—" : `${v}%`} />} />
                {domains.map((d) => (
                  <Line
                    key={d.key}
                    type="monotone"
                    dataKey={d.key}
                    name={d.label}
                    stroke={colors[d.key]}
                    strokeWidth={d.is_client ? 2.5 : 1.8}
                    dot={{ r: 2.5 }}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Domain rank line chart (lower = better) */}
      <div className="card-soft" data-testid="chart-domain-rank">
        <CardHeader
          icon={LineIcon}
          kicker="Domain rank"
          title="Domain rank over time"
          right={
            <div className="text-[10px] text-[#a0aec0] font-mono-tight uppercase tracking-widest">
              lower = stronger
            </div>
          }
        />
        {rangeCount < 1 ? (
          <EmptyState msg="No snapshots in this range." />
        ) : !domainRankHasAnyValue ? (
          <EmptyState msg="Domain rank not available — this metric requires DataForSEO Backlinks tier or Moz API. The rest of the competitor charts still work." />
        ) : (
          <div className="p-3 h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={domainRankData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#a0aec0" }} stroke="#cbd5e0" />
                <YAxis
                  reversed
                  allowDecimals={false}
                  tick={{ fontSize: 10, fill: "#a0aec0" }}
                  stroke="#cbd5e0"
                />
                <Tooltip content={<TooltipBox />} />
                {domains.map((d) => (
                  <Line
                    key={d.key}
                    type="monotone"
                    dataKey={d.key}
                    name={d.label}
                    stroke={colors[d.key]}
                    strokeWidth={d.is_client ? 2.5 : 1.8}
                    dot={{ r: 2.5 }}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Top 5 keyword comparison — grouped bar */}
      <div className="card-soft" data-testid="chart-top5-bar">
        <CardHeader
          icon={BarChart3}
          kicker="Top 5 keyword comparison"
          title="Where you stack up · latest snapshot"
          right={
            <div className="text-[10px] text-[#a0aec0] font-mono-tight uppercase tracking-widest">
              lower bar = better rank
            </div>
          }
        />
        {top5BarData.length === 0 ? (
          <EmptyState msg="No keyword data in latest snapshot." />
        ) : (
          <>
            <div className="px-5 pt-4">
              <DomainLegend domains={domains} colors={colors} />
            </div>
            <div className="p-3 h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={top5BarData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                  <XAxis
                    dataKey="keyword"
                    tick={{ fontSize: 10, fill: "#4a5568" }}
                    stroke="#cbd5e0"
                    angle={-12}
                    textAnchor="end"
                    height={50}
                    interval={0}
                  />
                  <YAxis
                    reversed
                    allowDecimals={false}
                    tick={{ fontSize: 10, fill: "#a0aec0" }}
                    stroke="#cbd5e0"
                    label={{ value: "Position", angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#a0aec0" } }}
                  />
                  <Tooltip content={<TopBarTooltip domains={domains} />} />
                  {domains.map((d) => (
                    <Bar
                      key={d.key}
                      dataKey={d.key}
                      name={d.label}
                      fill={colors[d.key]}
                      radius={[4, 4, 0, 0]}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>

      {/* Rank distribution stacked area (per-target selector) */}
      <div className="card-soft" data-testid="chart-rank-distribution">
        <CardHeader
          icon={Layers}
          kicker="Rank distribution"
          title="Keyword rank buckets over time"
          right={
            <TargetPicker
              value={stackTarget}
              onChange={setStackTarget}
              domains={domains}
              colors={colors}
            />
          }
        />
        {rangeCount < 1 ? (
          <EmptyState msg="No snapshots in this range." />
        ) : (
          <div className="p-3 h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stackData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="rkTop3" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#38b2ac" stopOpacity={0.95} />
                    <stop offset="100%" stopColor="#38b2ac" stopOpacity={0.7} />
                  </linearGradient>
                  <linearGradient id="rkTop10" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4fd1c5" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#4fd1c5" stopOpacity={0.6} />
                  </linearGradient>
                  <linearGradient id="rkTop30" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f6ad55" stopOpacity={0.85} />
                    <stop offset="100%" stopColor="#f6ad55" stopOpacity={0.5} />
                  </linearGradient>
                  <linearGradient id="rkBeyond" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fc8181" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="#fc8181" stopOpacity={0.45} />
                  </linearGradient>
                  <linearGradient id="rkUnranked" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#cbd5e0" stopOpacity={0.75} />
                    <stop offset="100%" stopColor="#cbd5e0" stopOpacity={0.4} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#a0aec0" }} stroke="#cbd5e0" />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#a0aec0" }} stroke="#cbd5e0" />
                <Tooltip content={<TooltipBox />} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                <Area type="monotone" stackId="r" dataKey="top3" name="Top 3" stroke="#38b2ac" fill="url(#rkTop3)" isAnimationActive={false} />
                <Area type="monotone" stackId="r" dataKey="top10" name="4–10" stroke="#4fd1c5" fill="url(#rkTop10)" isAnimationActive={false} />
                <Area type="monotone" stackId="r" dataKey="top30" name="11–30" stroke="#f6ad55" fill="url(#rkTop30)" isAnimationActive={false} />
                <Area type="monotone" stackId="r" dataKey="beyond" name="31+" stroke="#fc8181" fill="url(#rkBeyond)" isAnimationActive={false} />
                <Area type="monotone" stackId="r" dataKey="unranked" name="Unranked" stroke="#cbd5e0" fill="url(#rkUnranked)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function DomainLegend({ domains, colors }) {
  return (
    <div className="flex flex-wrap gap-2 justify-end">
      {domains.map((d) => (
        <div
          key={d.key}
          className={`inline-flex items-center gap-1.5 text-[10px] font-mono-tight px-2 py-1 rounded-md ${
            d.is_client ? "bg-[#4fd1c5]/10 text-[#234e52]" : "bg-[#f7fafc] text-[#4a5568]"
          }`}
        >
          <span className="h-2 w-2 rounded-full" style={{ background: colors[d.key] }} />
          {d.is_client && <Crown className="h-2.5 w-2.5" />}
          <span className="truncate max-w-[140px]">{d.label}</span>
        </div>
      ))}
    </div>
  );
}

function TopBarTooltip({ active, payload, label, domains }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl shadow-sm px-3 py-2 text-xs">
      <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold mb-1 max-w-[220px] truncate">{label}</div>
      <div className="space-y-0.5">
        {payload.map((p) => {
          const raw = p.payload[`${p.dataKey}__raw`];
          return (
            <div key={p.dataKey} className="flex items-center gap-2 font-mono-tight">
              <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
              <span className="text-[#4a5568] truncate max-w-[180px]">{p.name}</span>
              <span className="ml-auto text-[#1a202c] font-semibold">
                {raw == null ? "Unranked" : `#${raw}`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TargetPicker({ value, onChange, domains, colors }) {
  const [open, setOpen] = useState(false);
  const current = domains.find((d) => d.key === value) || domains[0];
  if (!current) return null;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="rank-distribution-target-picker"
        className="inline-flex items-center gap-2 bg-white border border-[#e2e8f0] rounded-lg px-3 py-1.5 text-xs font-mono-tight hover:border-[#4fd1c5] transition-colors"
      >
        <span className="h-2 w-2 rounded-full" style={{ background: colors[current.key] }} />
        {current.is_client && <Crown className="h-3 w-3 text-[#2c7a7b]" />}
        <span className="truncate max-w-[160px]">{current.label}</span>
        <ChevronDown className="h-3 w-3 text-[#a0aec0]" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 max-h-60 overflow-y-auto bg-white border border-[#e2e8f0] rounded-xl shadow-md z-20 py-1">
          {domains.map((d) => (
            <button
              key={d.key}
              onClick={() => { onChange(d.key); setOpen(false); }}
              data-testid={`rank-distribution-target-${d.key}`}
              className={`w-full text-left px-3 py-1.5 text-xs font-mono-tight inline-flex items-center gap-2 hover:bg-[#f7fafc] ${
                d.key === value ? "bg-[#4fd1c5]/10 text-[#234e52]" : "text-[#4a5568]"
              }`}
            >
              <span className="h-2 w-2 rounded-full" style={{ background: colors[d.key] }} />
              {d.is_client && <Crown className="h-3 w-3 text-[#2c7a7b]" />}
              <span className="truncate">{d.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
